import { useState, useEffect } from "react";
import { X, Loader2, CheckSquare, Square, Table2, ChevronLeft, UserCircle2, CheckCircle2 } from "lucide-react";
import { toast } from "react-hot-toast";
import spreadsheetConnectorService, {
  Credential,
  SheetFile,
  SpreadsheetConnector,
  ListCredentialsResult,
} from "../../../services/spreadsheet-connector.service";

type Step = 0 | 1 | 2;

interface ConnectSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentStoreId: string;
  /** Set by the parent when returning from OAuth — skips straight to step 2 */
  credentialId?: string;
  onCreated: (connectors: SpreadsheetConnector[]) => void;
}

const STEP_TITLES: Record<Step, string> = {
  0: "Connect Google Sheets",
  1: "Authorize with Google",
  2: "Select Spreadsheets",
};

export default function ConnectSheetModal({
  isOpen,
  onClose,
  documentStoreId,
  credentialId,
  onCreated,
}: ConnectSheetModalProps) {
  const [step, setStep] = useState<Step>(0);

  // Step 0
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);

  // Step 1
  const [authLoading, setAuthLoading] = useState(false);

  // Step 2
  const [activeCredentialId, setActiveCredentialId] = useState("");
  const [sheets, setSheets] = useState<SheetFile[]>([]);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // ── On open ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Reset sheet picker state every time the modal opens
    setSelectedIds([]);
    setSheets([]);

    if (credentialId) {
      // Returning from OAuth — jump straight to sheet picker
      setActiveCredentialId(credentialId);
      setStep(2);
      loadSheets(credentialId);
    } else {
      // Normal open — show credential chooser first
      setStep(0);
      loadCredentials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, credentialId]);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const result: ListCredentialsResult = await spreadsheetConnectorService.listCredentials(documentStoreId);
      setCredentials(result.data);
      // Nothing saved yet → skip the chooser and go straight to OAuth
      if (result.data.length === 0) setStep(1);
    } catch {
      toast.error("Failed to load saved accounts");
      setStep(1); // fallback so the user can still connect
    } finally {
      setCredentialsLoading(false);
    }
  };

  const loadSheets = async (credId: string) => {
    setSheetsLoading(true);
    try {
      const data = await spreadsheetConnectorService.listSheetFiles(credId);
      setSheets(data);
    } catch {
      toast.error("Failed to load Google Sheets files");
    } finally {
      setSheetsLoading(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePickCredential = (id: string) => {
    setActiveCredentialId(id);
    setSelectedIds([]);
    setSheets([]);
    setStep(2);
    loadSheets(id);
  };

  const handleAuthorize = async () => {
    setAuthLoading(true);
    try {
      const { auth_url } = await spreadsheetConnectorService.getAuthUrl(documentStoreId);
      sessionStorage.setItem("sc_document_store_id", documentStoreId);
      window.location.href = auth_url;
    } catch {
      toast.error("Failed to get Google authorization URL");
      setAuthLoading(false);
    }
  };

  const selectableSheets = sheets.filter((s) => !s.is_connected);

  const toggleSheet = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedIds(
      selectedIds.length === selectableSheets.length
        ? []
        : selectableSheets.map((s) => s.spreadsheet_id)
    );

  const handleSubmit = async () => {
    if (!activeCredentialId) {
      toast.error("No account selected. Please go back and pick a Google account.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Select at least one spreadsheet");
      return;
    }
    setSubmitting(true);
    try {
      const created = await spreadsheetConnectorService.createConnectors({
        credential_id: activeCredentialId,
        spreadsheet_ids: selectedIds,
      });
      toast.success(`Connected ${created.length} spreadsheet${created.length !== 1 ? "s" : ""}`);
      onCreated(created);
    } catch {
      toast.error("Failed to create connector");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => setStep(credentials.length > 0 ? 0 : 1);

  const isBusy = submitting || authLoading;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={() => { if (!isBusy) onClose(); }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            {step > 0 && !credentialId && (
              <button
                onClick={handleBack}
                disabled={isBusy}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors mr-1"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Table2 className="w-4 h-4 text-green-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">{STEP_TITLES[step]}</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isBusy}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── Step 0 — Credential chooser ── */}
          {step === 0 && (
            credentialsLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading saved accounts…</span>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Pick a saved Google account or connect a new one.
                </p>

                {/* Existing credential list */}
                <div className="space-y-2">
                  {credentials.map((cred) => (
                    <button
                      key={cred._id}
                      onClick={() => handlePickCredential(cred._id)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 text-left transition-colors group"
                    >
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition-colors">
                        <UserCircle2 className="w-5 h-5 text-gray-500 group-hover:text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{cred.user_name}</p>
                        <p className="text-xs text-gray-400 truncate">{cred.user_email}</p>
                      </div>
                      <ChevronLeft className="w-4 h-4 text-gray-300 group-hover:text-green-500 rotate-180 transition-colors" />
                    </button>
                  ))}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">or</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {/* Connect new account */}
                <button
                  onClick={() => setStep(1)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:border-green-400 hover:text-green-700 hover:bg-green-50 transition-colors"
                >
                  + Connect new Google account
                </button>
              </div>
            )
          )}

          {/* ── Step 1 — OAuth ── */}
          {step === 1 && (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <Table2 className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Connect your Google account</p>
                <p className="text-sm text-gray-500">
                  Authorize DMS to access your Google Sheets. You'll be redirected to Google and
                  brought back automatically after sign-in.
                </p>
              </div>
              <button
                onClick={handleAuthorize}
                disabled={authLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {authLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting…
                  </>
                ) : (
                  "Authorize with Google"
                )}
              </button>
            </div>
          )}

          {/* ── Step 2 — Sheet picker ── */}
          {step === 2 && (
            sheetsLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading your spreadsheets…</span>
              </div>
            ) : sheets.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-10">
                No Google Sheets files found in this account.
              </p>
            ) : (
              <div className="space-y-4">
                {/* Select all toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {sheets.length} file{sheets.length !== 1 ? "s" : ""} found
                    {selectableSheets.length < sheets.length && (
                      <span className="text-gray-400">
                        {" "}· {sheets.length - selectableSheets.length} already connected
                      </span>
                    )}
                  </span>
                  {selectableSheets.length > 0 && (
                    <button
                      onClick={toggleAll}
                      className="text-xs font-medium text-green-600 hover:text-green-700 transition-colors"
                    >
                      {selectedIds.length === selectableSheets.length ? "Deselect all" : "Select all"}
                    </button>
                  )}
                </div>

                {/* Sheet list */}
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {sheets.map((sheet) => {
                    const connected = sheet.is_connected;
                    const selected = selectedIds.includes(sheet.spreadsheet_id);
                    return connected ? (
                      /* Already connected — non-interactive */
                      <div
                        key={sheet.spreadsheet_id}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 bg-gray-50 text-left cursor-not-allowed"
                      >
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-400 truncate">{sheet.name}</p>
                          <p className="text-xs text-gray-400">Modified {formatDate(sheet.modified_at)}</p>
                        </div>
                        <span className="text-xs font-medium text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex-shrink-0">
                          Connected
                        </span>
                      </div>
                    ) : (
                      /* Selectable */
                      <button
                        key={sheet.spreadsheet_id}
                        onClick={() => toggleSheet(sheet.spreadsheet_id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                          selected
                            ? "border-green-300 bg-green-50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {selected ? (
                          <CheckSquare className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{sheet.name}</p>
                          <p className="text-xs text-gray-400">Modified {formatDate(sheet.modified_at)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>

              </div>
            )
          )}
        </div>

        {/* ── Footer — only on step 2 with sheets loaded ── */}
        {step === 2 && !sheetsLoading && sheets.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              {selectedIds.length} of {selectableSheets.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || selectedIds.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connecting…
                  </>
                ) : (
                  "Connect"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
